"use client";

import { formatWorkspaceDate } from "@/lib/display-preferences";

import { useMemo, useState } from "react";
import type { ConnectorSnapshot } from "@/lib/connectors/types";
import { systems, type SystemId } from "@/lib/systems";
import type { TimelineEvidenceEvent } from "@/lib/timeline-types";

type SystemsWorkspaceProps = {
  connectors: ConnectorSnapshot[];
  events: TimelineEvidenceEvent[];
  loading: boolean;
  focusId?: string | null;
};

function sourceStatus(connector: ConnectorSnapshot | undefined, loading: boolean) {
  if (loading) return "Checking";
  if (!connector) return "Not registered";
  if (connector.status === "live") return "Connected";
  if (connector.status === "registered") return "Registered";
  if (connector.status === "credentials_required") return "Needs credentials";
  return "Unavailable";
}

function eventDate(value: string) {
  return formatWorkspaceDate(new Date(value), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function TermList({ items }: { items: string[] }) {
  return (
    <ul className="system-term-list">
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}

export default function SystemsWorkspace({
  connectors,
  events,
  loading,
  focusId,
}: SystemsWorkspaceProps) {
  const [tab, setTab] = useState<"overview" | "mechanisms" | "evidence">("mechanisms");
  const [selectedId, setSelectedId] = useState<SystemId>(() =>
    focusId && systems.some((system) => system.id === focusId)
      ? focusId as SystemId
      : systems[0].id,
  );
  const selected = systems.find((system) => system.id === selectedId) ?? systems[0];

  const connectorById = useMemo(
    () => new Map(connectors.map((connector) => [connector.id, connector])),
    [connectors],
  );

  const evidenceEvents = useMemo(() => {
    const linked = new Set(selected.sourceLinks.map((source) => source.id));
    return events.filter((event) => event.sourceIds.some((sourceId) => linked.has(sourceId)));
  }, [events, selected]);

  const connectedSources = selected.sourceLinks.filter(
    (source) => connectorById.get(source.id)?.status === "live",
  ).length;

  const observedActors = useMemo(() => {
    const relevantSystemIds = new Set<string>([
      selected.id,
      ...(selected.memberSystemIds ?? []),
    ]);
    return connectors.flatMap((connector) => {
      const model = connector.incentiveModel;
      if (!model) return [];
      const positions = model.positions.filter((position) =>
        position.systemIds.some((systemId) => relevantSystemIds.has(systemId)),
      );
      const economicTies = model.economicTies.filter((tie) =>
        tie.systemIds.some((systemId) => relevantSystemIds.has(systemId)),
      );
      if (!positions.length && !economicTies.length) return [];
      return [{ connector, model, positions, economicTies }];
    });
  }, [connectors, selected]);

  return <section className="workspace systems" id="workspace" aria-labelledby="systems-title">
    <header className="page-heading"><div><h1 id="systems-title"><select className="heading-select" aria-label="System model" value={selected.id} onChange={event => setSelectedId(event.target.value as SystemId)}>{systems.map(system => <option value={system.id} key={system.id}>{system.name}</option>)}</select></h1><p className="lede">Draft model · {selected.sourceLinks.length} linked sources</p></div><button type="button" className="primary-button" onClick={() => setTab("evidence")}>Open evidence</button></header>
    <p className="system-purpose">{selected.purpose}</p>
    <nav className="quiet-tabs" aria-label="System sections">{(["overview", "mechanisms", "evidence"] as const).map(value => <button type="button" key={value} aria-pressed={tab === value} onClick={() => setTab(value)}>{value[0].toUpperCase() + value.slice(1)}</button>)}</nav>
    <div className="system-reading-grid"><article className="system-reading">
      {tab === "mechanisms" && <div className="mechanism-reading-list">{selected.mechanisms.map((mechanism, index) => <details key={`${selected.id}-${mechanism.name}`} open={index === 0}><summary>{mechanism.name}</summary><p>{mechanism.description}</p></details>)}</div>}
      {tab === "overview" && <><h2>Inside the system</h2><p>{selected.boundary.inside}</p><details><summary>Constituents</summary><TermList items={selected.constituents}/></details><details><summary>State variables</summary><TermList items={selected.stateVariables}/></details>{selected.memberSystemIds && <section><h3>Member systems</h3>{selected.memberSystemIds.map(id => <button type="button" key={id} onClick={() => setSelectedId(id)}>{systems.find(system => system.id === id)?.name ?? id}</button>)}</section>}
      <details><summary>Incentive hypotheses</summary>{selected.incentiveStructures?.length ? selected.incentiveStructures.map(incentive => <section key={incentive.actorClass}><h3>{incentive.actorClass}</h3><h4>Possible objectives</h4><TermList items={incentive.objectiveHypotheses}/><h4>Constraints</h4><TermList items={incentive.constraints}/><h4>Evidence needed</h4><TermList items={incentive.evidenceNeeded}/></section>) : <p className="muted">No incentive model recorded.</p>}<p className="muted">Objectives are competing hypotheses, not attributed motives.</p></details>
      <details><summary>Observed actor positions</summary>{observedActors.length ? observedActors.map(({ connector, model, positions, economicTies }) => <section key={connector.id}><h3>{model.sourceNodeName}</h3>{positions.map(position => <div key={position.id}><strong>{position.targetName}</strong><p>{position.role}</p><small>{position.evidenceCode} · {position.state} · {position.authorityLevers.join(" · ")}</small></div>)}{economicTies.map(tie => <p key={tie.id}>{tie.targetName} · {tie.type.replaceAll("_", " ")}</p>)}</section>) : <p className="muted">No evidence-bounded actor records linked yet.</p>}</details></>}
      {tab === "evidence" && <><h2>Evidence for the mechanism</h2><div className="system-evidence-reading">{evidenceEvents.length ? evidenceEvents.map(event => <details key={event.id}><summary><strong>{event.title}</strong><span>Unclassified</span></summary><p>{event.detail}</p><small>{event.kind} · {eventDate(event.at)}</small>{event.url && <p><a href={event.url} target="_blank" rel="noreferrer">Open source ↗</a></p>}</details>) : <p className="muted">No linked events are ready for classification.</p>}</div><h3>What is missing?</h3><TermList items={selected.coverageGaps}/><details><summary>Source coverage · {connectedSources} connected</summary><ul className="system-source-list">{selected.sourceLinks.map(source => <li key={source.id}><strong>{source.label}</strong><span>{sourceStatus(connectorById.get(source.id), loading)}</span></li>)}</ul></details><p className="muted">Source association alone does not establish support or contradiction.</p></>}
      <details className="system-scope"><summary>Scope and assumptions</summary><p>{selected.scope}</p><h3>Outside unless linked</h3><p>{selected.boundary.outside}</p><p className="muted">{selected.type} · {selected.coverage}</p></details>
      <details><summary>Stocks and flows</summary><h3>Stocks</h3><TermList items={selected.stocks}/><h3>Flows</h3><TermList items={selected.flows}/></details>
      <details><summary>Rules and constraints</summary><TermList items={selected.rules}/><h3>Dependencies</h3><TermList items={selected.dependencies}/></details>
      <details><summary>Feedback and delays</summary>{selected.feedbackLoops.map(loop => <section key={loop.name}><h3>{loop.name}</h3><p>{loop.cycle}</p><small>{loop.delay}</small></section>)}</details>
    </article><aside className="system-watch"><h3>{tab === "evidence" ? "Scope" : "Watch"}</h3>{tab === "evidence" ? <p>{selected.boundary.inside}</p> : <TermList items={selected.indicators}/>}<p className="muted">Draft model. Relationships remain hypotheses.</p></aside></div>
  </section>;
}
